import { DeployEnv } from '@patsnap-utils/biz';
import { REGION } from '@eureka/utils/environment';
import type { EditionType, PlanConfig } from '../types';
import { STRIPE_PRICE_IDS } from './price-ids';

export { STRIPE_PRICE_IDS } from './price-ids';
export {
    DEFAULT_CREDITS_PRICE_REGION,
    CREDITS_TIER_PRICE_OPTIONS,
    fetchCurrentCreditsPriceRegion,
    getCreditsStripePriceId,
    getCreditsTierPriceOption,
    resolveCreditsPriceRegion,
    resolveCreditsTierFromPriceId,
} from './credits-pricing-region';
export type { CreditsPriceRegion, CreditsBillingCycle, StripeDeployEnv } from './credits-pricing-region';

export const isSandBoxEnv = [DeployEnv.CI, DeployEnv.QA, DeployEnv.STAGE, DeployEnv.DEV].includes(REGION)
export const isOversea = [DeployEnv.US, DeployEnv.EU].includes(REGION)
export const isUS = REGION === DeployEnv.US;
export const isEU = REGION === DeployEnv.EU;
export const isStage = REGION === DeployEnv.STAGE;
export const isCn = REGION === DeployEnv.CN;
export const isOverseaOrStage = isOversea || isStage;

/**
 * 套餐价格表 (RD)
 */
export const PRICE_MAP: Record<EditionType, { priceId: string, billingCycle: 'monthly' | 'yearly' }[]> = {
    basic: [],
    rd_pro: [
        {
            priceId: isUS ? STRIPE_PRICE_IDS.rd.pro_monthly.us : (isEU ? STRIPE_PRICE_IDS.rd.pro_monthly.eu : (isStage ? STRIPE_PRICE_IDS.rd.pro_monthly.stage : STRIPE_PRICE_IDS.rd.pro_monthly.cn)),
            billingCycle: 'monthly',
        },
        {
            priceId: isUS ? STRIPE_PRICE_IDS.rd.pro_yearly.us : (isEU ? STRIPE_PRICE_IDS.rd.pro_yearly.eu : (isStage ? STRIPE_PRICE_IDS.rd.pro_yearly.stage : STRIPE_PRICE_IDS.rd.pro_yearly.cn)),
            billingCycle: 'yearly',
        }
    ],
    enterprise: [],
    api: [],
}

/**
 * MSE 套餐价格表（仅 STAGE / US / EU 环境上线）
 */
export const MSE_PRICE_MAP = {
    pro_monthly: isStage ? STRIPE_PRICE_IDS.mse.pro_monthly.stage : (isUS ? STRIPE_PRICE_IDS.mse.pro_monthly.us : (isEU ? STRIPE_PRICE_IDS.mse.pro_monthly.eu : STRIPE_PRICE_IDS.mse.pro_monthly.cn)),
    pro_yearly: isStage ? STRIPE_PRICE_IDS.mse.pro_yearly.stage : (isUS ? STRIPE_PRICE_IDS.mse.pro_yearly.us : (isEU ? STRIPE_PRICE_IDS.mse.pro_yearly.eu : STRIPE_PRICE_IDS.mse.pro_yearly.cn)),
}

/**
 * LS 套餐价格表
 * 分 A / B 两个套餐, 根据ab-test/group接口返回确定使用哪个
 */
const LS_PRICE_MAP_ECONOMY = {
    pro_monthly: isStage ? STRIPE_PRICE_IDS.ls_economy.pro_monthly.stage : (isUS ? STRIPE_PRICE_IDS.ls_economy.pro_monthly.us : (isEU ? STRIPE_PRICE_IDS.ls_economy.pro_monthly.eu : STRIPE_PRICE_IDS.ls_economy.pro_monthly.cn)),
    pro_yearly: isStage ? STRIPE_PRICE_IDS.ls_economy.pro_yearly.stage : (isUS ? STRIPE_PRICE_IDS.ls_economy.pro_yearly.us : (isEU ? STRIPE_PRICE_IDS.ls_economy.pro_yearly.eu : STRIPE_PRICE_IDS.ls_economy.pro_yearly.cn)),
}

const LS_PRICE_MAP_STANDARD = {
    pro_monthly: isStage ? STRIPE_PRICE_IDS.ls_standard.pro_monthly.stage : (isUS ? STRIPE_PRICE_IDS.ls_standard.pro_monthly.us : (isEU ? STRIPE_PRICE_IDS.ls_standard.pro_monthly.eu : STRIPE_PRICE_IDS.ls_standard.pro_monthly.cn)),
    pro_yearly: isStage ? STRIPE_PRICE_IDS.ls_standard.pro_yearly.stage : (isUS ? STRIPE_PRICE_IDS.ls_standard.pro_yearly.us : (isEU ? STRIPE_PRICE_IDS.ls_standard.pro_yearly.eu : STRIPE_PRICE_IDS.ls_standard.pro_yearly.cn)),
}

export const LS_PRICE_MAP = {
    economy: LS_PRICE_MAP_ECONOMY,
    standard: LS_PRICE_MAP_STANDARD,
}

export const PLAN_CONFIG_MAP: Record<EditionType, PlanConfig> = {
    basic: {
        credits: 10,
        originalPrice: 0,
        salePrice: 0,
    },
    rd_pro: {
        credits: 100,
        originalPrice: 398,
        salePrice: 100,
        yearlyOriginalPrice: 1200, // 100 * 12
        yearlySalePrice: 1080, // 90 * 12 (相当于优惠120)
        emergencyPackOriginalPrice: 25,
        emergencyPackSaleDiscount: 20,
        emergencyPackSalePrice: 20,
        projectPackOriginalPrice: 55,
        projectPackSalePrice: 44,
        projectPackSaleDiscount: 20,
    },
    enterprise: {
        credits: 100,
        originalPrice: 398,
        salePrice: 99,
        yearlyOriginalPrice: 4776, // 398 * 12
        yearlySalePrice: 199, // 199 * 10 (相当于优惠2个月)
        emergencyPackOriginalPrice: 25,
        emergencyPackSaleDiscount: 20,
        emergencyPackSalePrice: 20,
        projectPackOriginalPrice: 55,
        projectPackSalePrice: 44,
        projectPackSaleDiscount: 20,
    },
    api: {
        credits: 100,
        originalPrice: 398,
        salePrice: 99,
        yearlyOriginalPrice: 4776, // 398 * 12
        yearlySalePrice: 199, // 199 * 10 (相当于优惠2个月)
        emergencyPackOriginalPrice: 25,
        emergencyPackSaleDiscount: 20,
        emergencyPackSalePrice: 20,
        projectPackOriginalPrice: 55,
        projectPackSalePrice: 44,
        projectPackSaleDiscount: 20,
    },
}
